import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from './Icon';
import './CategoryFilter.css';
import './DomainFilter.css';

const DomainFilter = ({ domains = [], excludedDomains = [], clientDomain = '', onChange, displayMode = 'exclusion' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredDomains = useMemo(() => {
    if (!searchQuery.trim()) return domains;
    const q = searchQuery.toLowerCase();
    return domains.filter(d => d.toLowerCase().includes(q));
  }, [domains, searchQuery]);

  const includedDomains = useMemo(
    () => domains.filter(d => !excludedDomains.includes(d)),
    [domains, excludedDomains]
  );
  const selectedCount = includedDomains.length;
  const isSelectionMode = displayMode === 'selection';

  const defaultExcludedInSelectionMode = useMemo(
    () => (clientDomain ? domains.filter(d => d !== clientDomain) : domains.slice()),
    [domains, clientDomain]
  );

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setSearchQuery('');
  };

  const handleDomainToggle = (domain) => {
    const isExcluded = excludedDomains.includes(domain);
    const newExcluded = isExcluded
      ? excludedDomains.filter(d => d !== domain)
      : [...excludedDomains, domain];
    onChange(newExcluded);
  };

  const handleClearAll = () => {
    if (isSelectionMode) {
      onChange(defaultExcludedInSelectionMode);
    } else {
      onChange([]);
    }
  };

  const handleSelectAll = () => {
    setSearchQuery('');
    onChange([]);
  };

  const handleDeselectAll = () => {
    const allDomains = [...domains];
    setSearchQuery('');
    onChange(allDomains);
  };

  const displayText = isSelectionMode
    ? selectedCount === 0
      ? '0 domaine sélectionné'
      : selectedCount === 1
        ? '1 domaine sélectionné'
        : `${selectedCount} domaines sélectionnés`
    : excludedDomains.length === 0
      ? 'Tous les domaines'
      : `${excludedDomains.length} exclu${excludedDomains.length > 1 ? 's' : ''}`;

  const isAlertState = isSelectionMode ? selectedCount === 0 : excludedDomains.length > 0;

  const isAtDefaultState = isSelectionMode
    ? selectedCount === 1
      && clientDomain
      && !excludedDomains.includes(clientDomain)
    : excludedDomains.length === 0;

  if (!domains || domains.length === 0) {
    return null;
  }

  return (
    <div className="domain-filter" ref={dropdownRef}>
      <button
        className={`domain-filter-trigger ${isAlertState ? 'has-exclusion' : ''}`}
        onClick={handleToggle}
        type="button"
      >
        <Icon name="Globe" size={16} />
        <span className="filter-text">{displayText}</span>
        <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} size={14} />
      </button>

      {isOpen && (
        <div className="domain-filter-dropdown">
          <div className="domain-filter-header">
            <button className="filter-action-btn" onClick={handleSelectAll} type="button">
              <Icon name="CheckSquare" size={16} />
              Tout sélectionner
            </button>
            <button className="filter-action-btn" onClick={handleDeselectAll} type="button">
              <Icon name="Square" size={16} />
              Tout désélectionner
            </button>
          </div>

          <div className="domain-filter-search">
            <Icon name="Search" size={14} className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un domaine..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {!isAtDefaultState && (
              <button
                className="domain-filter-reset"
                onClick={handleClearAll}
                type="button"
                title="Réinitialiser"
              >
                <Icon name="RotateCcw" size={12} />
              </button>
            )}
          </div>

          <div className="domain-filter-list">
            {filteredDomains.map((domain) => {
              const isExcluded = excludedDomains.includes(domain);
              const isClient = domain === clientDomain;
              return (
                <label 
                  key={domain} 
                  className={`domain-filter-item ${isExcluded ? 'excluded' : ''} ${isClient ? 'is-client' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={!isExcluded}
                    onChange={() => handleDomainToggle(domain)}
                  />
                  <Icon name={isExcluded ? "Square" : "CheckSquare"} size={16} />
                  <span className="domain-name">
                    {domain}
                    {isClient && <span className="client-badge">Client</span>}
                  </span>
                </label>
              );
            })}
            {filteredDomains.length === 0 && (
              <div className="domain-filter-empty">Aucun domaine trouvé</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainFilter;
